import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoAddOutline } from "react-icons/io5";
import { HiOutlineSearch } from "react-icons/hi";
import { MdClear, MdDriveFileRenameOutline, MdPreview } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";

import Button from "../Utilities/Button";
import EventStatusBadge from "../EventManagement/EventStatusBadge";
import PaginationNew from "../Utilities/PaginationNew";
import RightPanelLayout from "../Layout/RightPanelLayout";
import Alert from "../Utilities/Alert";
import Toast from "../Utilities/Toast";

import styles from "./style/SystemAnnouncementsList.module.scss";
import WebsiteAnnouncementPreviewModal from "./WebsiteAnnouncementPreviewModal";
import {
  fetchWebsiteAnnouncementById,
  fetchWebsiteAnnouncementsList,
  deleteWebsiteAnnouncement,
} from "./systemAnnouncementsApi";
import {
  EMPTY_DISPLAY,
  formatPublishWindow,
  getAnnouncementStatusBadgeTheme,
  getAnnouncementStatusTypeLabels,
  getAnnouncementTypeBadgeTheme,
  normalizeWebsiteAnnouncementListRow,
} from "./systemAnnouncementsTransforms";

const LIMIT = 10;

const mutedUnset = (value) =>
  value === EMPTY_DISPLAY ? (
    <span className={styles.placeholderText}>{EMPTY_DISPLAY}</span>
  ) : (
    value
  );

const renderDateTimeBlock = (dateStr, timeStr, blockClassName) => {
  const bothUnset =
    dateStr === EMPTY_DISPLAY && timeStr === EMPTY_DISPLAY;
  if (bothUnset) {
    return (
      <div className={blockClassName}>
        <div className='whitespace-nowrap'>{mutedUnset(EMPTY_DISPLAY)}</div>
      </div>
    );
  }
  return (
    <div className={blockClassName}>
      <div className='whitespace-nowrap'>{mutedUnset(dateStr)}</div>
      <div>{mutedUnset(timeStr)}</div>
    </div>
  );
};

const SystemAnnouncementsList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");

  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDetail, setPreviewDetail] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);

  const [toastState, setToastState] = useState({
    show: false,
    title: "",
    subtitle: "",
    error: false,
  });

  const showToast = (title, error = false) => {
    setToastState({ show: true, title, subtitle: "", error });
    window.setTimeout(() => {
      setToastState((s) => ({ ...s, show: false }));
    }, 3200);
  };

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await fetchWebsiteAnnouncementsList({
        search: searchApplied,
        page,
        limit: LIMIT,
      });
      const apiRows = data?.rows ?? [];
      setRows(apiRows.map(normalizeWebsiteAnnouncementListRow));
      setTotal(Number(data?.total) || 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setListLoading(false);
    }
  }, [page, searchApplied]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / LIMIT)),
    [total],
  );

  const onPageChange = (selectedObj) => {
    setPage(selectedObj.selected + 1);
  };

  const applyListSearch = () => {
    setSearchApplied(searchInput.trim());
    setPage(1);
  };

  const handleOpenPreview = async (id) => {
    setPreviewLoadingId(id);
    try {
      const data = await fetchWebsiteAnnouncementById(id);
      setPreviewDetail(data);
      setPreviewOpen(true);
    } catch {
      showToast("載入預覽失敗請重試", true);
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewDetail(null);
  };

  const handleDelete = async () => {
    if (!deleteTargetId || deleteSubmitting) return;
    setDeleteSubmitting(true);
    try {
      await deleteWebsiteAnnouncement(deleteTargetId);
      setDeleteTargetId(null);
      showToast("已刪除系統公告", false);
      await loadList();
    } catch {
      showToast("刪除失敗請重試", true);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <RightPanelLayout title='系統公告'>
      <div className={styles.listToolbar}>
        <div className={styles.listToolbarTitle}>系統公告列表</div>
        <div className={styles.toolbarActions}>
          <div className={styles.searchBar}>
            <button
              type='button'
              className={styles.searchBarIconBtn}
              onClick={applyListSearch}
              aria-label='搜尋'
            >
              <HiOutlineSearch className={styles.searchBarIcon} />
            </button>
            <input
              type='search'
              className={styles.searchBarInput}
              value={searchInput}
              placeholder='搜尋公告名稱'
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  applyListSearch();
                }
              }}
              aria-label='搜尋公告名稱'
            />
            {searchInput ? (
              <button
                type='button'
                className={styles.searchBarClear}
                onClick={() => {
                  setSearchInput("");
                  setSearchApplied("");
                  setPage(1);
                }}
                aria-label='清除搜尋'
              >
                <MdClear className={styles.searchBarClearIcon} />
              </button>
            ) : null}
          </div>
          <Button type='primary' title='搜尋' onClick={applyListSearch} />
          <Button
            type='primary'
            title='新增'
            icon={<IoAddOutline fontSize={20} />}
            onClick={() =>
              navigate("/dashboard/tdp/system-announcements/new")
            }
          />
        </div>
      </div>

      {!listLoading && total > 0 ? (
        <div className={styles.tablePaginationTop}>
          <PaginationNew
            onPageChange={onPageChange}
            pageCount={page}
            limit={LIMIT}
            totalPages={totalPages}
            totalData={total}
          />
        </div>
      ) : null}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>標題</th>
              <th>狀態</th>
              <th>類型</th>
              <th>上架時間</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {listLoading ? (
              <tr>
                <td colSpan={5} className={styles.tableLoadingCell}>
                  載入中…
                </td>
              </tr>
            ) : null}

            {!listLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={5} className={styles.tableEmptyCell}>
                  尚無系統公告資料
                </td>
              </tr>
            ) : null}

            {!listLoading
              ? rows.map((item) => {
                  const { statusLabel, typeLabel } =
                    getAnnouncementStatusTypeLabels(item);
                  const statusTheme = getAnnouncementStatusBadgeTheme(statusLabel);
                  const typeTheme = getAnnouncementTypeBadgeTheme(typeLabel);
                  const publishWindow = formatPublishWindow(
                    item.publishedAt,
                    item.unpublishedAt,
                  );
                  return (
                    <tr key={item.id}>
                      <td className={styles.tableTitle}>
                        {item.title || EMPTY_DISPLAY}
                      </td>
                      <td>
                        <EventStatusBadge
                          theme={statusTheme}
                          text={statusLabel}
                        />
                      </td>
                      <td>
                        <EventStatusBadge theme={typeTheme} text={typeLabel} />
                      </td>
                      <td>
                        <div className={styles.registrationTimeCell}>
                          {renderDateTimeBlock(
                            publishWindow.startDate,
                            publishWindow.startTime,
                            styles.registrationTimeBlock,
                          )}
                          <span aria-hidden='true'>-</span>
                          {renderDateTimeBlock(
                            publishWindow.endDate,
                            publishWindow.endTime,
                            styles.registrationTimeBlock,
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={styles.actions}>
                          <button
                            type='button'
                            className={styles.viewButton}
                            onClick={() => handleOpenPreview(item.id)}
                            disabled={previewLoadingId === item.id}
                            aria-label='預覽系統公告'
                          >
                            <MdPreview />
                          </button>
                          <button
                            type='button'
                            className={styles.editButton}
                            onClick={() =>
                              navigate(
                                `/dashboard/tdp/system-announcements/${item.id}/edit`,
                              )
                            }
                            aria-label='編輯系統公告'
                          >
                            <MdDriveFileRenameOutline />
                          </button>
                          <button
                            type='button'
                            className={styles.deleteButton}
                            onClick={() => setDeleteTargetId(item.id)}
                            aria-label='刪除系統公告'
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              : null}
          </tbody>
        </table>
      </div>

      <Alert
        isOpen={deleteTargetId != null}
        onClose={() => !deleteSubmitting && setDeleteTargetId(null)}
        onConfirm={handleDelete}
        leftButtonText='取消'
        rightButtonText={deleteSubmitting ? "刪除中…" : "刪除"}
        mainTitle='確認刪除系統公告？'
        subTitle='刪除後無法復原。'
        isWarning
      />

      <WebsiteAnnouncementPreviewModal
        isOpen={previewOpen}
        onClose={handleClosePreview}
        title={previewDetail?.title ?? ""}
        contentHtml={previewDetail?.content ?? ""}
        displayType={previewDetail?.display_type}
        redirectButtonText={
          previewDetail?.url_context ??
          previewDetail?.cta_label ??
          previewDetail?.cta_button_text ??
          previewDetail?.button_text ??
          previewDetail?.meta_json?.cta_label ??
          ""
        }
        redirectUrl={
          previewDetail?.url_path ??
          previewDetail?.cta_url ??
          previewDetail?.link_url ??
          previewDetail?.redirect_url ??
          previewDetail?.meta_json?.cta_url ??
          ""
        }
      />

      <Toast
        show={toastState.show}
        title={toastState.title}
        subtitle={toastState.subtitle}
        error={toastState.error}
      />
    </RightPanelLayout>
  );
};

export default SystemAnnouncementsList;
